import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import FileUploader from "./FileUploader";
import { MessagesSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Messaging panel for a lead.
 * - Agents see a thread selector (per DMC) if there are multiple bidders.
 * - DMCs / MICE Operators only see their own thread with the agent.
 */
export default function MessagesPanel({ lead, bids }) {
  const { user } = useAuth();
  const [threadWith, setThreadWith] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const listRef = useRef(null);

  const isAgent = user?.role === "agent" && lead?.agent_id === user?.user_id;
  const isDmc = user?.role === "dmc" || user?.role === "mice_operator";
  // Unique bidders for agent thread selector
  const dmcs = [];
  const seen = new Set();
  (bids || []).forEach((b) => {
    if (!seen.has(b.dmc_id)) { seen.add(b.dmc_id); dmcs.push({ user_id: b.dmc_id, name: b.dmc_name, country: b.dmc_country }); }
  });

  useEffect(() => {
    if (isAgent && !threadWith && dmcs.length) setThreadWith(dmcs[0].user_id);
  }, [isAgent, dmcs.length]); // eslint-disable-line

  const load = async () => {
    if (!lead) return;
    const params = isAgent && threadWith ? { thread_with: threadWith } : {};
    try {
      const r = await api.get(`/leads/${lead.lead_id}/messages`, { params });
      setMsgs(r.data || []);
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 50);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, [lead?.lead_id, threadWith]); // eslint-disable-line

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() && !attachments.length) return;
    setBusy(true);
    try {
      const payload = { body: body.trim() || "(attachment)" };
      if (isAgent) {
        if (!threadWith) { toast.error("Pick a DMC thread"); setBusy(false); return; }
        payload.to_user_id = threadWith;
      }
      // Include attachment references as [file:{id}:{name}] tokens in body for now
      if (attachments.length) {
        const tokens = attachments.map((a) => `[file:${a.file_id}:${a.original_filename}]`).join(" ");
        payload.body = `${payload.body}\n${tokens}`;
      }
      await api.post(`/leads/${lead.lead_id}/messages`, payload);
      setBody("");
      setAttachments([]);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  // Nothing to show for admins or agents who aren't the owner
  if (!isAgent && !isDmc) return null;
  if (isAgent && dmcs.length === 0) {
    return (
      <div className="card-paper p-6" data-testid="messages-panel-empty">
        <div className="flex items-center gap-2">
          <MessagesSquare className="w-5 h-5 text-brand" />
          <p className="overline">Thread</p>
        </div>
        <p className="text-sm text-muted mt-3">Threads open once a DMC posts a bid.</p>
      </div>
    );
  }

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  return (
    <div className="card-paper" data-testid="messages-panel">
      <div className="p-5 border-b border-hairline flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MessagesSquare className="w-5 h-5 text-brand" />
          <p className="overline">Thread</p>
        </div>
        {isAgent && dmcs.length > 0 && (
          <select
            value={threadWith || ""}
            onChange={(e) => setThreadWith(e.target.value)}
            className="ml-auto text-sm py-1.5 px-3"
            data-testid="thread-selector"
          >
            {dmcs.map((d) => <option key={d.user_id} value={d.user_id}>{d.name} · {d.country || "—"}</option>)}
          </select>
        )}
      </div>

      <ul ref={listRef} className="p-5 max-h-96 overflow-y-auto space-y-3" data-testid="message-list">
        {msgs.length === 0 ? (
          <li className="text-sm text-muted text-center py-6">No messages yet. Start the conversation ↓</li>
        ) : msgs.map((m) => {
          const mine = m.from_user_id === user?.user_id;
          // Pull embedded file tokens
          const fileMatches = Array.from(m.body.matchAll(/\[file:([a-f0-9]+):([^\]]+)\]/g));
          const cleanBody = m.body.replace(/\[file:[a-f0-9]+:[^\]]+\]/g, "").trim();
          return (
            <li key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-md px-4 py-2.5 text-sm ${mine ? "bg-ink text-white" : "bg-ledger text-ink border border-hairline"}`}>
                <div className={`text-[10px] font-mono uppercase tracking-widest ${mine ? "text-white/60" : "text-muted"} mb-1`}>
                  {m.from_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
                {cleanBody && <div className="whitespace-pre-wrap break-words">{cleanBody}</div>}
                {fileMatches.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {fileMatches.map(([, fid, fname]) => (
                      <li key={fid}>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fid}?auth=${token || ""}`}
                          target="_blank" rel="noreferrer"
                          className={`text-xs underline underline-offset-2 ${mine ? "text-white" : "text-brand"}`}
                        >📎 {fname}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <form onSubmit={send} className="p-4 border-t border-hairline space-y-2" data-testid="message-composer">
        <textarea
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message. Ops is copied on ops@trawelldmc.com"
          className="w-full text-sm"
          data-testid="message-body"
        />
        <FileUploader value={attachments} onChange={setAttachments} purpose="attachment" testid="message-attach" />
        <div className="flex items-center justify-end">
          <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="send-message">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {busy ? "Sending" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}
